import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Phone, Mail, Menu, X } from 'lucide-react';
import Logo from '../ui/Logo';
import { trackPhoneClick, trackEmailClick } from '../../utils/analytics';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handlePhoneClick = () => {
    trackPhoneClick('+79137701139');
  };

  const handleEmailClick = () => {
    trackEmailClick('prominnova@mail.ru');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      {/* Top bar with contacts */}
      <div className="bg-primary-900 text-white py-2 hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <a
              href="tel:+79137701139"
              className="flex items-center text-sm hover:text-primary-200 transition-colors"
              onClick={handlePhoneClick}
            >
              <Phone className="h-4 w-4 mr-2" />
              <span>+7 (913) 770-11-39</span>
            </a>
            <a
              href="mailto:prominnova@mail.ru"
              className="flex items-center text-sm hover:text-primary-200 transition-colors"
              onClick={handleEmailClick}
            >
              <Mail className="h-4 w-4 mr-2" />
              <span>prominnova@mail.ru</span>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm">Пн-Пт: 10:00 - 22:00</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`container-custom py-4 ${isScrolled ? 'py-3' : 'py-4'}`}>
        <div className="flex justify-between items-center">
          <Link to="/" className="flex-shrink-0" onClick={closeMenu}>
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <NavLink
              to="/catalog"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              Каталог
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              О компании
            </NavLink>
            <NavLink
              to="/advantages"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              Преимущества
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              Услуги
            </NavLink>
            <NavLink
              to="/projects"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              Проекты
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `transition hover:text-primary-600 ${isActive ? 'text-primary-600 font-medium' : 'text-gray-700'}`
              }
            >
              Контакты
            </NavLink>
          </div>

          {/* Call to action button */}
          <div className="hidden md:block">
            <Link to="/contact" className="btn-primary">
              Оставить заявку
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-primary-600 focus:outline-none"
            onClick={toggleMenu}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden ${isMenuOpen ? 'block' : 'hidden'}`}>
          <div className="pt-2 pb-4 space-y-1">
            <NavLink
              to="/catalog"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              Каталог
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              О компании
            </NavLink>
            <NavLink
              to="/advantages"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              Преимущества
            </NavLink>
            <NavLink
              to="/services"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              Услуги
            </NavLink>
            <NavLink
              to="/projects"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              Проекты
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `block py-2 px-3 rounded-md ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700 hover:bg-gray-50'}`
              }
              onClick={closeMenu}
            >
              Контакты
            </NavLink>
            <div className="pt-4">
              <Link
                to="/contact"
                className="w-full block text-center btn-primary"
                onClick={closeMenu}
              >
                Оставить заявку
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
